import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, FileText, AlertTriangle } from 'lucide-react';
import { invoiceIntakeService, type SupplierInvoice, type InvoiceStatus } from '@/lib/invoiceIntakeService';
import { useCompany } from '@/context/CompanyContext';

type FilterTab = 'all' | InvoiceStatus;

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; classes: string }> = {
  pending_review: { label: 'ממתינה', classes: 'bg-amber-100 text-amber-800' },
  confirmed: { label: 'מאושרת', classes: 'bg-green-100 text-green-800' },
  rejected: { label: 'נדחתה', classes: 'bg-red-100 text-red-800' },
};

const TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'הכל' },
  { key: 'pending_review', label: 'ממתינות לאישור' },
  { key: 'confirmed', label: 'מאושרות' },
  { key: 'rejected', label: 'נדחו' },
];

function daysSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function formatCurrency(amount: number | null): string {
  if (amount == null) return '—';
  return amount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
}

export function InvoiceHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeCompany } = useCompany();

  const [invoices, setInvoices] = useState<SupplierInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!activeCompany?.id) return;
    setLoading(true);
    invoiceIntakeService
      .getInvoices(activeCompany.id, statusFilter === 'all' ? undefined : statusFilter)
      .then(({ data }) => setInvoices(data ?? []))
      .finally(() => setLoading(false));
  }, [activeCompany?.id, statusFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices;
    const q = search.trim().toLowerCase();
    return invoices.filter(
      (inv) =>
        inv.supplier_name_raw?.toLowerCase().includes(q) ||
        inv.invoice_number?.toLowerCase().includes(q)
    );
  }, [invoices, search]);

  const handleRowClick = (id: string) => navigate(`/app/invoice-intake/review/${id}`);

  return (
    <div dir="rtl" className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-xl">
          <FileText className="w-8 h-8 text-teal-700" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('invoiceIntake.history.title', 'היסטוריית חשבוניות')}
        </h1>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors ${
              statusFilter === tab.key
                ? 'text-teal-700 bg-teal-50 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder={t('invoiceIntake.history.search', 'חיפוש לפי ספק או מספר חשבונית...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pr-10 pl-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {t('invoiceIntake.history.empty', 'לא נמצאו חשבוניות התואמות את החיפוש')}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto bg-white rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-right px-4 py-3 font-medium">ספק</th>
                  <th className="text-right px-4 py-3 font-medium">מספר חשבונית</th>
                  <th className="text-right px-4 py-3 font-medium">תאריך</th>
                  <th className="text-right px-4 py-3 font-medium">סכום</th>
                  <th className="text-right px-4 py-3 font-medium">מע&quot;מ</th>
                  <th className="text-right px-4 py-3 font-medium">סטטוס</th>
                  <th className="text-right px-4 py-3 font-medium">גיל</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((inv) => {
                  const age = daysSince(inv.invoice_date);
                  const cfg = STATUS_CONFIG[inv.status];
                  return (
                    <tr
                      key={inv.id}
                      onClick={() => handleRowClick(inv.id)}
                      className="hover:bg-teal-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{inv.supplier_name_raw ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{inv.invoice_number ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{inv.invoice_date ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-900">{formatCurrency(inv.total_amount)}</td>
                      <td className="px-4 py-3 text-gray-600">{formatCurrency(inv.total_vat)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.classes}`}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="flex items-center gap-1">
                          {age != null ? `${age} ימים` : '—'}
                          {age != null && age > 180 && (
                            <span title="חשבונית ישנה - מעל 180 יום">
                              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                            </span>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {filtered.map((inv) => {
              const age = daysSince(inv.invoice_date);
              const cfg = STATUS_CONFIG[inv.status];
              return (
                <button
                  key={inv.id}
                  onClick={() => handleRowClick(inv.id)}
                  className="w-full text-right bg-white rounded-xl border border-gray-200 p-4 space-y-2 hover:border-teal-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.classes}`}>
                      {cfg.label}
                    </span>
                    <span className="font-semibold text-gray-900">{inv.supplier_name_raw ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>{inv.invoice_date ?? '—'}</span>
                    <span>{inv.invoice_number ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-gray-500">
                      {age != null ? `${age} ימים` : '—'}
                      {age != null && age > 180 && (
                        <span title="חשבונית ישנה - מעל 180 יום">
                          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                        </span>
                      )}
                    </div>
                    <span className="font-medium text-gray-900">{formatCurrency(inv.total_amount)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
